import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { fetchPatchDetails, fetchReport } from '@/api/reports'
import { ColumnPicker } from '@/components/ColumnPicker'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { ReportTable } from '@/components/ReportTable'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getReportColumns } from '@/config/columns'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { cn } from '@/lib/utils'

const STATUS_LABELS = {
  installed: 'Installed updates',
  pending: 'Pending updates',
  failed: 'Failed updates',
}

function formatStatusLabel(status) {
  return STATUS_LABELS[status] || status
}

export function PatchStatusReport({ customerIds, onExportNote }) {
  const [summary, setSummary] = useState([])
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [details, setDetails] = useState([])
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [error, setError] = useState('')

  const detailColumns = getReportColumns('patch-details')
  const {
    visibleIds,
    visibleColumns,
    toggleColumn,
    showAll,
    resetDefaults,
  } = useColumnVisibility('report-columns-patch-details', detailColumns)

  const chartData = useMemo(
    () =>
      summary.map((row) => ({
        label: row.status,
        value: row.count || 0,
      })),
    [summary],
  )

  useEffect(() => {
    setSummaryLoading(true)
    setError('')
    setSelectedStatus(null)
    fetchReport('patch-status', {}, customerIds)
      .then((data) => setSummary(Array.isArray(data) ? data : []))
      .catch(() => {
        setError('Could not load patch summary.')
        setSummary([])
      })
      .finally(() => setSummaryLoading(false))
  }, [customerIds])

  useEffect(() => {
    if (!selectedStatus) {
      setDetails([])
      return
    }
    setDetailsLoading(true)
    fetchPatchDetails(selectedStatus, customerIds)
      .then((data) => setDetails(Array.isArray(data) ? data : []))
      .catch(() => setError('Could not load patch details.'))
      .finally(() => setDetailsLoading(false))
  }, [selectedStatus, customerIds])

  useEffect(() => {
    onExportNote?.(
      'CSV export includes every patch row (all statuses) with device and customer.',
    )
  }, [onExportNote])

  function toggleStatus(status) {
    setSelectedStatus((current) => (current === status ? null : status))
  }

  if (summaryLoading) {
    return <Skeleton className="h-48 w-full" />
  }

  const totalPatches = summary.reduce((n, row) => n + (row.count || 0), 0)

  return (
    <div className="space-y-6">
      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <p className="text-sm text-muted-foreground">
        Patch data from Atera includes update title, KB identifier, class/severity, and
        status (installed, pending, or failed). Click a status in the table or chart to list
        matching updates per device. Total patches in scope: {totalPatches.toLocaleString('en-GB')}.
      </p>

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <HorizontalScrollArea>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Count</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                    No patch data. Run a sync from Admin first.
                  </td>
                </tr>
              ) : (
                summary.map((row) => {
                  const isSelected = selectedStatus === row.status
                  return (
                    <tr
                      key={row.status}
                      className={cn(
                        'cursor-pointer border-b border-border/60 transition-colors hover:bg-muted/30',
                        isSelected && 'bg-primary/10',
                      )}
                      onClick={() => toggleStatus(row.status)}
                    >
                      <td className="px-4 py-3 font-medium capitalize">
                        {formatStatusLabel(row.status)}
                      </td>
                      <td className="px-4 py-3">{row.count?.toLocaleString('en-GB')}</td>
                      <td className="px-4 py-3 text-xs text-primary">
                        {isSelected ? 'Hide detail' : 'View devices'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </HorizontalScrollArea>

        <div className="min-h-[240px] rounded-lg border border-border bg-card p-4">
          <SimplePieChart
            data={chartData}
            formatLabel={formatStatusLabel}
            showLegend={false}
            activeLabel={selectedStatus}
            onItemClick={toggleStatus}
            emptyMessage="No patch data"
          />
        </div>
      </div>

      {selectedStatus ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold">
              {formatStatusLabel(selectedStatus)}
              {details.length > 0 ? (
                <span className="ml-2 font-normal text-muted-foreground">
                  ({details.length.toLocaleString('en-GB')} rows)
                </span>
              ) : null}
            </h3>
            <div className="flex items-center gap-2">
              {detailColumns.length > 0 ? (
                <ColumnPicker
                  columns={detailColumns}
                  visibleIds={visibleIds}
                  onToggle={toggleColumn}
                  onShowAll={showAll}
                  onResetDefaults={resetDefaults}
                />
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => setSelectedStatus(null)}>
                <X className="h-4 w-4" />
                Close
              </Button>
            </div>
          </div>
          {detailsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ReportTable
              key={selectedStatus || 'patch-details'}
              rows={details}
              visibleColumns={visibleColumns}
              defaultSort={{ id: 'hostname', dir: 'asc' }}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}
